import omitBy from 'lodash/omitBy';

import {
  ComputedNodeStatus,
  NodeResourceOffering,
  NodeResourceOfferingWithSoleTenancy,
  NodeStatePhase,
  NodeStatus,
  NodeStorageMetrics,
  Node,
} from '@cast/types';
import { bytesToUnit, getTimeAgo, toDateTimePrecise } from '@cast/utils';

import { hasDisruptedTaint } from 'utils/api-utils';
import { getResourceUsagePercentage } from 'utils/node';

import { NodeWithDependencies } from '../types';
import { PreparedNode } from './types';
import { getGpuCount } from '../_utils/getGpuCount';
import {
  calculateBaseCostPerCPU,
  calculateCostPerCPU,
  calculateInstanceCost,
} from '../_utils/nodeCost';

import { makeEnumAggregateFn } from '@cast/shared-search-ui';

export const prepareNode = (
  node: NodeWithDependencies,
  storageMetrics?: NodeStorageMetrics,
  isCastAnywhere?: boolean
): PreparedNode => {
  const time = node.createdAt || node.joinedAt!;
  const formattedDate = toDateTimePrecise(time);
  const distance = getTimeAgo(time, true);

  const resources = node.resources;
  let cpuPercentage: number | undefined;
  let memoryPercentage: number | undefined;
  if (resources) {
    const {
      cpuRequestsMilli,
      cpuAllocatableMilli,
      memRequestsMib,
      memAllocatableMib,
    } = resources;
    cpuPercentage = getResourceUsagePercentage(
      cpuRequestsMilli,
      cpuAllocatableMilli
    );
    memoryPercentage = getResourceUsagePercentage(
      memRequestsMib,
      memAllocatableMib
    );
  }

  const [memory, memoryUnit] = bytesToUnit(
    node.resources?.memCapacityMib
      ? node.resources.memCapacityMib * Math.pow(1024, 2)
      : 0,
    2
  );

  const totalCpuPrice = calculateCostPerCPU({
    node,
    costPeriod: 'hourly',
    isCastAnywhere,
  });
  const baseCpuPrice = calculateBaseCostPerCPU({
    node,
    costPeriod: 'hourly',
    isCastAnywhere,
  });

  let ephemeralStorage: string | undefined;
  let ephemeralStoragePercentage: number | undefined;
  let ephemeralStorageAllocatableBytes: number | undefined;
  let ephemeralStorageRequestedBytes: number | undefined;
  let ephemeralStorageUsedBytes: number | undefined;

  if (storageMetrics) {
    ephemeralStorageAllocatableBytes = storageMetrics.allocatableBytes
      ? Number(storageMetrics.allocatableBytes)
      : undefined;
    ephemeralStorageRequestedBytes = storageMetrics.requestedBytes
      ? Number(storageMetrics.requestedBytes)
      : undefined;
    ephemeralStorageUsedBytes = storageMetrics.usedBytes
      ? Number(storageMetrics.usedBytes)
      : undefined;

    if (ephemeralStorageAllocatableBytes) {
      const [value, unit] = bytesToUnit(ephemeralStorageAllocatableBytes, 2);
      ephemeralStorage = value ? `${value} ${unit}` : undefined;
    }

    if (
      ephemeralStorageUsedBytes !== undefined &&
      ephemeralStorageAllocatableBytes
    ) {
      ephemeralStoragePercentage = getResourceUsagePercentage(
        ephemeralStorageUsedBytes,
        ephemeralStorageAllocatableBytes
      );
    }
  }

  return {
    ...node,
    cpu: node.resources?.cpuCapacityMilli
      ? `${node.resources?.cpuCapacityMilli / 1000}`
      : '-',
    memory: memory ? `${memory} ${memoryUnit}` : '-',
    cpuPercentage,
    memoryPercentage,
    ephemeralStorage,
    ephemeralStoragePercentage,
    ephemeralStorageAllocatableBytes,
    ephemeralStorageRequestedBytes,
    ephemeralStorageUsedBytes,
    preparedTimeAgo: {
      formattedDate,
      distance,
    },
    totalCpuPrice: totalCpuPrice.toString(),
    baseCpuPrice: baseCpuPrice.toString(),
    gpuCount: getGpuCount(node),
  };
};

export const isRebalanceAnnotation = (annotation: string): boolean => {
  return annotation.includes('rebalancing.cast.ai');
};

export const isEvictorAnnotation = (annotation: string): boolean => {
  return annotation.includes('evictor.cast.ai');
};

export const statusSelectAggregateFn = (
  entity: NodeWithDependencies[]
): Record<string, number> => {
  const enumAggregated = makeEnumAggregateFn(
    // TODO: remove 'not-ready' when GRAV-366 is done
    {
      ...NodeStatus,
      ...ComputedNodeStatus,
      'not-ready': 'not-ready',
    },
    (n) => n.state.phase
  )(entity);

  if (enumAggregated['not-ready'] !== 0) {
    enumAggregated.notReady = enumAggregated['not-ready'];
    enumAggregated['not-ready'] = 0;
  }

  return omitBy(enumAggregated, (value) => value === 0);
};

export const addSelectedCriterionInAvailableStatuses = (
  filterValue: string[] | undefined,
  aggregatedStatuses: Record<string | number, number>
): Record<string | number, number> => {
  let availableStatuses = { ...aggregatedStatuses };

  filterValue?.forEach((criterionStatus) => {
    if (!aggregatedStatuses[criterionStatus]) {
      availableStatuses = { ...availableStatuses, [criterionStatus]: 0 };
    }
  });

  return availableStatuses;
};

export const aggregateNodeCountByResourceOffering = (
  nodes: PreparedNode[]
): Record<NodeResourceOfferingWithSoleTenancy, number> => {
  return nodes.reduce(
    (acc, node) => {
      acc[node.resourceOffering] = acc[node.resourceOffering] + 1;
      return acc;
    },
    {
      [NodeResourceOffering.ON_DEMAND]: 0,
      [NodeResourceOffering.SPOT]: 0,
      [NodeResourceOffering.FALLBACK]: 0,
      'sole-tenant': 0,
    }
  );
};

export const aggregateNodeCountByStatus = (
  nodes: PreparedNode[]
): Record<NodeStatePhase, number> => {
  return nodes.reduce<Record<NodeStatePhase, number>>(
    (acc, node) => {
      if (node.state.phase) {
        acc[node.state.phase] = acc[node.state.phase] + 1;
      } else {
        acc.unknown = acc.unknown + 1;
      }
      return acc;
    },
    {
      creating: 0,
      pending: 0,
      ready: 0,
      'not-ready': 0,
      draining: 0,
      deleting: 0,
      deleted: 0,
      interrupted: 0,
      unknown: 0,
      [ComputedNodeStatus.CORDONED]: 0,
      [ComputedNodeStatus.DISRUPTED]: 0,
      [NodeStatus.detached]: 0,
    }
  );
};

export const calculateNodesMonthlyCost = (nodes: PreparedNode[]): number => {
  return nodes.reduce(
    (acc, node) => acc + calculateInstanceCost({ node, costPeriod: 'monthly' }),
    0
  );
};

export const isNodeUnschedulable = (node: Node): boolean => {
  return node.unschedulable || hasDisruptedTaint(node);
};
